"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoCloseOutline, IoChevronDownOutline } from "react-icons/io5";
import {
  Loader2,
  AlertCircle,
  Image as ImageIcon,
  FileText,
  Home,
  DollarSign,
  MapPin,
  Ruler,
  Calendar,
  Users,
  Building,
  Check,
  Tag,
} from "lucide-react";
import cities from "./cities.json";
import toast from "react-hot-toast";
import {
  FaDollarSign,
  FaEuroSign,
  FaPoundSign,
  FaRubleSign,
  FaShekelSign,
  FaYenSign,
} from "react-icons/fa";
import { REST_API_URL } from "../../api/config";

const API_ROOT = REST_API_URL;

const CURRENCY_ICON_MAP = {
  USD: FaDollarSign,
  EUR: FaEuroSign,
  GBP: FaPoundSign,
  RUB: FaRubleSign,
  ILS: FaShekelSign,

  AUD: FaDollarSign,
  CAD: FaDollarSign,
  BRL: FaDollarSign,
  MXN: FaDollarSign,
  AED: FaDollarSign,
  ARS: FaDollarSign,
  ZAR: FaDollarSign,
};

const PROPERTY_CLASS_OPTIONS = [
  { value: "Residential", label: "Residential" },
  { value: "Commercial", label: "Commercial" },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: "Apartment", label: "Apartment" },
  { value: "Villa", label: "Villa" },
  { value: "Townhouse", label: "Townhouse" },
  { value: "Penthouse", label: "Penthouse" },
];

const BEDROOM_OPTIONS = [
  { value: "Studio", label: "Studio" },
  { value: "1 Bedroom", label: "1 Bedroom" },
  { value: "2 Bedrooms", label: "2 Bedrooms" },
  { value: "3 Bedrooms", label: "3 Bedrooms" },
];

const BATHROOM_OPTIONS = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5+" },
];

const TOUR_DURATIONS = [
  { value: "15 minutes", label: "15 minutes" },
  { value: "30 minutes", label: "30 minutes" },
  { value: "60 minutes", label: "1 hour" },
  { value: "120 minutes", label: "2 hours" },
  { value: "custom", label: "Other" },
];

const BUFFER_TIMES = [
  { value: "15 minutes", label: "15 minutes" },
  { value: "30 minutes", label: "30 minutes" },
  { value: "60 minutes", label: "1 hour" },
  { value: "120 minutes", label: "2 hours" },
  { value: "custom", label: "Other" },
];

const OUTDOOR_TAGS = [
  { label: "Living Room", value: "living_room" },
  { label: "Kitchen", value: "kitchen" },
  { label: "Dining Room", value: "dining_room" },
  { label: "Bedroom", value: "bedroom" },
  { label: "Master Bedroom", value: "master_bedroom" },
  { label: "Bathroom", value: "bathroom" },
  { label: "Toilet", value: "toilet" },
  { label: "Hallway", value: "hallway" },
  { label: "Entryway / Foyer", value: "entryway_foyer" },
  { label: "Laundry Room", value: "laundry_room" },
  { label: "Garage", value: "garage" },
  { label: "Storage Room / Closet", value: "storage_room_closet" },
  { label: "Home Office / Study", value: "home_office_study" },
  { label: "Guest Room", value: "guest_room" },
  { label: "Pantry", value: "pantry" },
  { label: "Playroom / Family Room", value: "playroom_family_room" },
  { label: "Balcony / Porch Garden", value: "balcony_porch_garden" },
];

const INDOOR_TAGS = [
  { label: "Playroom", value: "playroom" },
  { label: "Swimming Pool", value: "swimming_pool" },
  { label: "Gym", value: "gym" },
  { label: "Sports Court", value: "sports_court" },
  { label: "Garden", value: "garden" },
  { label: "Picnic Area", value: "picnic_area" },
  { label: "BBQ Zone", value: "bbq_zone" },
  { label: "Patio", value: "patio" },
  { label: "Outdoor Seating Area", value: "outdoor_seating_area" },
  { label: "Dog Park", value: "dog_park" },
  { label: "Game Room", value: "game_room" },
  { label: "Movie Room", value: "movie_room" },
  { label: "Library", value: "library" },
  { label: "Community Hall", value: "community_hall" },
  { label: "Bicycle Path", value: "bicycle_path" },
];

const MAX_IMAGES = 10;

const HANDOVER_QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const HANDOVER_YEARS = ["2026", "2027", "2028", "2029", "2030"];

const PAYMENT_PLANS = [
  "50/50",
  "60/40",
  "65/35",
  "70/30",
  "75/25",
  "80/20",
  "85/15",
  "90/10",
];

const getUserTimeZone = () => {
  if (typeof window === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

function TagSelect({
  value,
  onChange,
  options,
  placeholder = "Select category",
  disabled = false,
  type = "property",
  isUnknown = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label || placeholder;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg border transition-all ${
          isUnknown
            ? "border-red-300 bg-red-50 text-red-700 hover:border-red-400"
            : value
            ? "border-green-300 bg-green-50 text-green-700"
            : "border-gray-300 bg-gray-50 text-gray-700 hover:border-gray-400"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <div className="flex items-center gap-2 truncate">
          <Tag
            className={`w-3.5 h-3.5 ${
              isUnknown
                ? "text-red-500"
                : value
                ? "text-green-500"
                : "text-gray-400"
            }`}
          />
          <span className="truncate">{selectedLabel}</span>
        </div>
        <IoChevronDownOutline
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto"
          >
            <div className="p-1">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors ${
                    value === option.value
                      ? "bg-blue-50 text-blue-600"
                      : "text-gray-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function EditListingModal({ listing, onClose, onUpdated }) {
  const propertyImagesInputRef = useRef(null);
  const amenityImagesInputRef = useRef(null);
  const propertyInfoInputRef = useRef(null);
  const buildingInfoInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [apiErrors, setApiErrors] = useState([]);
  const today = new Date().toISOString().split("T")[0];
  const [isReady, setIsReady] = useState(true);
  const [listingType, setListingType] = useState("Rent");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [propertyClass, setPropertyClass] = useState("Residential");
  const [propertyType, setPropertyType] = useState("Apartment");
  const [monthlyRent, setMonthlyRent] = useState("");
  const [yearlyRent, setYearlyRent] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [handoverBy, setHandoverBy] = useState("");
  const [paymentPlan, setPaymentPlan] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [bedroom, setBedroom] = useState("Studio");
  const [bathroom, setBathroom] = useState("1");
  const [size, setSize] = useState("");
  const [availableFrom, setAvailableFrom] = useState(today);
  const [availableTo, setAvailableTo] = useState("");
  const [tourDuration, setTourDuration] = useState("30 minutes");
  const [bufferTime, setBufferTime] = useState("15 minutes");
  const [customTourDuration, setCustomTourDuration] = useState("");
  const [customBufferTime, setCustomBufferTime] = useState("");
  const [isCustomTour, setIsCustomTour] = useState(false);
  const [isCustomBuffer, setIsCustomBuffer] = useState(false);

  const [propertyImages, setPropertyImages] = useState([]);
  const [amenityImages, setAmenityImages] = useState([]);
  const [propertyInfoFiles, setPropertyInfoFiles] = useState([]);
  const [buildingInfoFiles, setBuildingInfoFiles] = useState([]);

  const [existingBuildingInfo, setExistingBuildingInfo] = useState(null);
  const [removeBuildingInfo, setRemoveBuildingInfo] = useState(false);
  const [existingPropertyInfo, setExistingPropertyInfo] = useState(null);
  const [removePropertyInfo, setRemovePropertyInfo] = useState(false);
  const [handoverQuarter, setHandoverQuarter] = useState("");
  const [handoverYear, setHandoverYear] = useState("");
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [showPaymentPlanModal, setShowPaymentPlanModal] = useState(false);

  const [currencies, setCurrencies] = useState([]);
  const [currency, setCurrency] = useState(null);

  const hasUnknownTags = () => {
    const newPropertyImages = propertyImages.filter((img) => !img.isExisting);
    const newAmenityImages = amenityImages.filter((img) => !img.isExisting);

    const unknownProperty = newPropertyImages.some(
      (img) => !img.aiLoading && (!img.tag || img.tag === "unknown")
    );

    const unknownAmenity = newAmenityImages.some(
      (img) => !img.aiLoading && (!img.tag || img.tag === "unknown")
    );

    return unknownProperty || unknownAmenity;
  };

  const unknownImagesCount = () => {
    const newPropertyImages = propertyImages.filter((img) => !img.isExisting);
    const newAmenityImages = amenityImages.filter((img) => !img.isExisting);

    const unknownProperty = newPropertyImages.filter(
      (img) => !img.aiLoading && (!img.tag || img.tag === "unknown")
    ).length;

    const unknownAmenity = newAmenityImages.filter(
      (img) => !img.aiLoading && (!img.tag || img.tag === "unknown")
    ).length;

    return unknownProperty + unknownAmenity;
  };

  useEffect(() => {
    if (listing) {

      setTitle(listing.property_name || "");
      setDescription(listing.property_description || "");
      setPropertyClass(listing.property_class || "Residential");
      setPropertyType(listing.property_type || "Apartment");
      setListingType(listing.listing_type || "Rent");

      setIsReady(!Boolean(listing.off_plan));

      if (listing.listing_type === "Sell") {
        setSellPrice(listing.sell_price || "");
      } else {
        setMonthlyRent(listing.monthly_rent_price || "");
        setYearlyRent(listing.yearly_rent_price || "");
        setAvailableFrom(listing.availability_from || today);
        setAvailableTo(listing.availability_till || "");
      }

      setHandoverBy(listing.handover_by || "");
      setPaymentPlan(listing.payment_plan || "");

      setCity(listing.city || "");
      setAddress(listing.address || "");

      setBedroom(listing.bedroom_type || "Studio");
      setBathroom(listing.bath_count || "1");

      setSize(listing.size || "");

      if (listing.tour_duration && listing.tour_duration_time_unit) {
        const duration = `${listing.tour_duration} ${listing.tour_duration_time_unit}`;
        const durationOption = TOUR_DURATIONS.find((t) => t.value === duration);
        if (durationOption) {
          setTourDuration(duration);
          setIsCustomTour(false);
        } else {
          setIsCustomTour(true);
          setCustomTourDuration(listing.tour_duration || "");
          setTourDuration("custom");
        }
      }

      if (listing.buffer_time && listing.buffer_time_time_unit) {
        const buffer = `${listing.buffer_time} ${listing.buffer_time_time_unit}`;
        const bufferOption = BUFFER_TIMES.find((b) => b.value === buffer);
        if (bufferOption) {
          setBufferTime(buffer);
          setIsCustomBuffer(false);
        } else {
          setIsCustomBuffer(true);
          setCustomBufferTime(listing.buffer_time || "");
          setBufferTime("custom");
        }
      }

      if (listing.property_images && Array.isArray(listing.property_images)) {
        const existingPropertyImgs = listing.property_images.map((img) => ({
          id: img.id,
          preview: img.image,
          tag: img.label || null,
          aiLoading: false,
          isExisting: true,
        }));
        setPropertyImages(existingPropertyImgs);
      }

      if (listing.amenities_images && Array.isArray(listing.amenities_images)) {
        const existingAmenityImgs = listing.amenities_images.map((img) => ({
          id: img.id,
          preview: img.image,
          tag: img.label || null,
          aiLoading: false,
          isExisting: true,
        }));
        setAmenityImages(existingAmenityImgs);
      }

      if (listing.building_info) {
        setExistingBuildingInfo({
          url: listing.building_info,
          name: listing.building_info.split("/").pop().split("?")[0],
        });
      }

      if (listing.property_info) {
        setExistingPropertyInfo({
          url: listing.property_info,
          name: listing.property_info.split("/").pop().split("?")[0],
        });
      }
    }
  }, [listing, today]);

 const validateForm = () => {
  const errors = [];

  if (!title.trim()) {
    errors.push("Property name is required");
  }
  if (!propertyClass) {
    errors.push("Property class is required");
  }
  if (!propertyType) {
    errors.push("Property type is required");
  }
  if (!bedroom) {
    errors.push("Number of bedrooms is required");
  }
  if (!city.trim()) {
    errors.push("City is required");
  }
  if (!address.trim()) {
    errors.push("Address is required");
  }
  if (!size || size === "0") {
    errors.push("Property size is required");
  }

  if (listingType === "Rent") {
    if (!monthlyRent && !yearlyRent) {
      errors.push("Either monthly or yearly rent is required");
    }
    
    // Add validation for availability dates
    if (availableFrom && availableTo) {
      const fromDate = new Date(availableFrom);
      const toDate = new Date(availableTo);
      
      if (toDate < fromDate) {
        errors.push("Availability 'Till' date cannot be earlier than 'From' date");
      }
    }
  }

  if (listingType === "Sell") {
    if (!sellPrice || sellPrice === "0") {
      errors.push("Sell price is required");
    }

    if (!isReady) {
      if (!handoverBy || !handoverBy.trim()) {
        errors.push("Handover period is required for Off Plan property");
      }
      if (!paymentPlan || !paymentPlan.trim()) {
        errors.push("Payment plan is required for Off Plan property");
      }
    }
  }

  if (hasUnknownTags()) {
    const count = unknownImagesCount();
    errors.push(
      `Please assign categories to ${count} image(s) marked as "Unknown" before updating`
    );
  }

  if (errors.length > 0) {
    errors.forEach((error) => {
      toast.error(error, {
        duration: 4000,
        position: "bottom-right",
        style: {
          background: "white",
          color: "#DC2626",
          borderRadius: "12px",
          fontSize: "14px",
          fontWeight: "500",
          maxWidth: "400px",
        },
      });
    });
    return false;
  }

  return true;
};

  const resetAllInputs = () => {
    [
      propertyImagesInputRef,
      amenityImagesInputRef,
      propertyInfoInputRef,
      buildingInfoInputRef,
    ].forEach((ref) => {
      if (ref.current) {
        ref.current.value = "";
      }
    });
  };

  const closeModal = () => {
    if (!loading) {
      resetAllInputs();
      onClose();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".city-dropdown-container")) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleTitleChange = (text) => {
    setTitle(text);
  };

  const handleDescriptionChange = (text) => {
    setDescription(text);
  };

  const handleMonthlyRentChange = (text) => {
    let value = text.replace(/[^0-9.]/g, "");
    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }

    const [integerPart, decimalPart] = value.split(".");
    if (integerPart.length > 13) return;

    if (decimalPart && decimalPart.length > 2) {
      value = `${integerPart}.${decimalPart.slice(0, 2)}`;
    }

    setMonthlyRent(value);
  };

  const handleYearlyRentChange = (text) => {
    let value = text.replace(/[^0-9.]/g, "");

    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }

    const [integerPart, decimalPart] = value.split(".");

    if (integerPart.length > 13) {
      return;
    }

    if (decimalPart && decimalPart.length > 2) {
      value = `${integerPart}.${decimalPart.slice(0, 2)}`;
    }

    setYearlyRent(value);
  };

  const handleSellPriceChange = (text) => {
    const cleanText = text.replace(/[^0-9.]/g, "");
    setSellPrice(cleanText);
  };

  const handleAddressChange = (text) => {
    setAddress(text);
  };

  const handleSizeChange = (text) => {
    const cleanText = text.replace(/[^0-9]/g, "");

    if (cleanText.length > 6) return;

    setSize(cleanText);
  };

  const handleHandoverByChange = (text) => {
    setHandoverBy(text);
  };

  const handlePaymentPlanChange = (text) => {
    setPaymentPlan(text);
  };

  const classifyImageAI = async (img, index, type) => {
    try {
      const formData = new FormData();
      formData.append("images", img.file);

      const response = await fetch(
        "https://fake-labeler-565506410896.europe-west4.run.app/classify/batch/",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`AI API error: ${response.status}`);
      }

      const data = await response.json();

      let label = "unknown";

      if (data.results && Array.isArray(data.results) && data.results[0]) {
        label = data.results[0].label || "unknown";
      } else if (data[0] && data[0].room && data[0].room.label) {
        label = data[0].room.label || "unknown";
      }

      const allowed =
        type === "property"
          ? OUTDOOR_TAGS.map((t) => t.value)
          : INDOOR_TAGS.map((t) => t.value);

      const validLabel = allowed.includes(label) ? label : "unknown";

      if (type === "property") {
        setPropertyImages((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            tag: validLabel,
            aiLoading: false,
            needsManualTag: validLabel === "unknown",
          };
          return updated;
        });
      } else {
        setAmenityImages((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            tag: validLabel,
            aiLoading: false,
            needsManualTag: validLabel === "unknown",
          };
          return updated;
        });
      }
    } catch (err) {
      console.error("AI classify error:", err);

      if (type === "property") {
        setPropertyImages((prev) => {
          const updated = [...prev];
          if (updated[index]) {
            updated[index] = {
              ...updated[index],
              tag: "unknown",
              aiLoading: false,
              needsManualTag: true,
            };
          }
          return updated;
        });
      } else {
        setAmenityImages((prev) => {
          const updated = [...prev];
          if (updated[index]) {
            updated[index] = {
              ...updated[index],
              tag: "unknown",
              aiLoading: false,
              needsManualTag: true,
            };
          }
          return updated;
        });
      }
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setFormErrors([]);
      setApiErrors([]);

      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!token) {
        toast.error("No authentication token found", {
          duration: 4000,
          position: "bottom-right",
          style: {
            background: "white",
            color: "#DC2626",
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: "500",
            maxWidth: "400px",
          },
        });
        return;
      }

      const loadingToastId = toast.loading("Updating listing...");

      const formData = new FormData();

      const textFields = {
        listing_type: listingType,
        property_name: title.trim(),
        property_description: description.trim(),
        property_class: propertyClass,
        property_type: propertyType,
        bedroom_type: bedroom,
        bath_count: parseInt(bathroom) || 1,
        city: city.split(",")[0].trim(),
        address: address.trim(),
        size: size || "0",
        tour_duration:
          parseInt(
            isCustomTour ? customTourDuration : tourDuration.split(" ")[0]
          ) || 30,
        tour_duration_time_unit: "minutes",
        buffer_time:
          parseInt(
            isCustomBuffer ? customBufferTime : bufferTime.split(" ")[0]
          ) || 15,
        buffer_time_time_unit: "minutes",
      };

      if (currency) {
        textFields.currency = currency;
      }

      Object.entries(textFields).forEach(([key, value]) => {
        formData.append(key, value.toString());
      });

      if (listingType === "Sell") {
        formData.append("sell_price", sellPrice || "0");
        formData.append("monthly_rent_price", "0");
        formData.append("yearly_rent_price", "0");
        formData.append("off_plan", !isReady ? "true" : "false");

        if (!isReady) {
          if (handoverBy) formData.append("handover_by", handoverBy);
          if (paymentPlan) formData.append("payment_plan", paymentPlan);
        }
      } else {
        formData.append("monthly_rent_price", monthlyRent || "0");
        formData.append("yearly_rent_price", yearlyRent || "0");
        formData.append("sell_price", "0");
        formData.append("off_plan", "false");

        if (availableFrom) {
          formData.append("availability_from", availableFrom);
        }
        if (availableTo) {
          formData.append("availability_till", availableTo);
        }
      }

      if (removeBuildingInfo) {
        formData.append("building_info_remove", "true");
      } else if (buildingInfoFiles.length > 0) {
        formData.append("building_info", buildingInfoFiles[0]);
      }

      if (removePropertyInfo) {
        formData.append("property_info_remove", "true");
      } else if (propertyInfoFiles.length > 0) {
        formData.append("property_info", propertyInfoFiles[0]);
      }

      const markedForRemovalProperty = propertyImages.filter(
        (img) => img.isExisting && img.markedForRemoval
      );
      const markedForRemovalAmenity = amenityImages.filter(
        (img) => img.isExisting && img.markedForRemoval
      );

      markedForRemovalProperty.forEach((img) => {
        formData.append("property_images_remove", img.id.toString());
      });

      markedForRemovalAmenity.forEach((img) => {
        formData.append("amenities_images_remove", img.id.toString());
      });

      const existingPropertyImagesToUpdate = propertyImages
        .filter(
          (img) =>
            img.isExisting &&
            img.id &&
            !img.markedForRemoval &&
            img.tag &&
            img.tag !== "unknown"
        )
        .map((img, index) => ({
          id: img.id,
          label: img.tag,
          index: index,
        }));

      const existingAmenityImagesToUpdate = amenityImages
        .filter(
          (img) =>
            img.isExisting &&
            img.id &&
            !img.markedForRemoval &&
            img.tag &&
            img.tag !== "unknown"
        )
        .map((img, index) => ({
          id: img.id,
          label: img.tag,
          index: index,
        }));

      existingPropertyImagesToUpdate.forEach((item, idx) => {
        formData.append(
          `property_images_update_labels[${idx}]id`,
          item.id.toString()
        );
        formData.append(
          `property_images_update_labels[${idx}]label`,
          item.label
        );
      });

      existingAmenityImagesToUpdate.forEach((item, idx) => {
        formData.append(
          `amenities_images_update_labels[${idx}]id`,
          item.id.toString()
        );
        formData.append(
          `amenities_images_update_labels[${idx}]label`,
          item.label
        );
      });

      const newPropertyImages = propertyImages
        .filter(
          (img) =>
            !img.isExisting && img.file && img.tag && img.tag !== "unknown"
        )
        .map((img, idx) => ({
          ...img,
          index: idx,
        }));

      const newAmenityImages = amenityImages
        .filter(
          (img) =>
            !img.isExisting && img.file && img.tag && img.tag !== "unknown"
        )
        .map((img, idx) => ({
          ...img,
          index: idx,
        }));

      newPropertyImages.forEach((img, idx) => {
        formData.append(`property_images_add[${idx}]image`, img.file);
        formData.append(`property_images_add[${idx}]label`, img.tag);
      });

      newAmenityImages.forEach((img, idx) => {
        formData.append(`amenities_images_add[${idx}]image`, img.file);
        formData.append(`amenities_images_add[${idx}]label`, img.tag);
      });

      const url = `${API_ROOT}/service/real-estate-my-listings/${listing.id}/`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "User-Timezone": getUserTimeZone(),
      };

      const res = await fetch(url, {
        method: "PUT",
        headers,
        body: formData,
      });

      if (!res.ok) {
        let errorMessage = `Server error (${res.status})`;

        try {
          const errorText = await res.text();
          console.error("Error response:", errorText);

          try {
            const errorData = JSON.parse(errorText);
            if (errorData.detail) {
              errorMessage = errorData.detail;
            } else if (typeof errorData === "object") {
              const fieldErrors = [];
              for (const [field, errors] of Object.entries(errorData)) {
                if (Array.isArray(errors)) {
                  fieldErrors.push(`${field}: ${errors.join(", ")}`);
                } else if (typeof errors === "object") {
                  for (const [subField, subErrors] of Object.entries(errors)) {
                    if (Array.isArray(subErrors)) {
                      fieldErrors.push(
                        `${field}.${subField}: ${subErrors.join(", ")}`
                      );
                    }
                  }
                }
              }

              if (fieldErrors.length > 0) {
                errorMessage = fieldErrors.join("; ");
              }
            }
          } catch {
            errorMessage = errorText.substring(0, 200);
          }
        } catch (e) {
          console.error("Error reading response:", e);
        }

        toast.dismiss(loadingToastId);
        toast.error(errorMessage, {
          duration: 4000,
          position: "bottom-right",
          style: {
            background: "white",
            color: "#DC2626",
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: "500",
            maxWidth: "400px",
          },
        });
        setApiErrors([errorMessage]);
        setLoading(false);
        return;
      }

      const data = await res.json();
      toast.dismiss(loadingToastId);
      toast.success("Listing updated successfully!", {
        duration: 3000,
        position: "bottom-right",
        style: {
          background: "white",
          color: "#059669",
          borderRadius: "12px",
          fontSize: "14px",
          fontWeight: "500",
          maxWidth: "400px",
        },
      });

      onUpdated?.();
      onClose();
    } catch (err) {
      console.error("Error updating listing:", err);
      toast.error(err.message || "An unexpected error occurred", {
        duration: 5000,
        position: "bottom-right",
        style: {
          background: "white",
          color: "#DC2626",
          borderRadius: "12px",
          fontSize: "14px",
          fontWeight: "500",
          maxWidth: "400px",
        },
      });
      setApiErrors([err.message]);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserCurrency = async () => {
      try {
        const token =
          localStorage.getItem("accessToken") ||
          sessionStorage.getItem("accessToken");

        if (!token) return;

        const headers = buildHeaders(token);

        const res = await fetch(`${API_ROOT}/auth/userinfo/`, {
          headers,
        });

        if (!res.ok) return;

        const data = await res.json();

        if (data?.currency) {
          setCurrency(data.currency); 
        }
      } catch (e) {
        console.error("Failed to fetch user currency", e);
      }
    };

    fetchUserCurrency();
  }, []);

  const CurrencyIcon = CURRENCY_ICON_MAP[currency] || DollarSign;
  const updateImageTag = (index, type, tag) => {
    if (type === "property") {
      setPropertyImages((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          tag,
          needsManualTag: false,
        };
        return updated;
      });
    } else {
      setAmenityImages((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          tag,
          needsManualTag: false,
        };
        return updated;
      });
    }
  };

  const removeImage = (index, type) => {
    if (type === "property") {
      setPropertyImages((prev) => {
        const updated = [...prev];
        const imgToRemove = updated[index];

        if (imgToRemove.preview && !imgToRemove.isExisting) {
          URL.revokeObjectURL(imgToRemove.preview);
        }

        if (imgToRemove.isExisting) {
          updated[index] = {
            ...imgToRemove,
            markedForRemoval: true,
          };
          if (propertyImagesInputRef.current) {
            propertyImagesInputRef.current.value = "";
          }
          return updated;
        } else {
          updated.splice(index, 1);
          if (propertyImagesInputRef.current) {
            propertyImagesInputRef.current.value = "";
          }
          return updated;
        }
      });
    } else {
      setAmenityImages((prev) => {
        const updated = [...prev];
        const imgToRemove = updated[index];

        if (imgToRemove.preview && !imgToRemove.isExisting) {
          URL.revokeObjectURL(imgToRemove.preview);
        }

        if (imgToRemove.isExisting) {
          updated[index] = {
            ...imgToRemove,
            markedForRemoval: true,
          };
          if (amenityImagesInputRef.current) {
            amenityImagesInputRef.current.value = "";
          }
          return updated;
        } else {
          updated.splice(index, 1);
          if (amenityImagesInputRef.current) {
            amenityImagesInputRef.current.value = "";
          }
          return updated;
        }
      });
    }
  };

  const removePropertyInfoFile = (index) => {
    setPropertyInfoFiles((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      if (propertyInfoInputRef.current) {
        propertyInfoInputRef.current.value = "";
      }
      return updated;
    });
  };

  const removeBuildingInfoFile = (index) => {
    setBuildingInfoFiles((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      if (buildingInfoInputRef.current) {
        buildingInfoInputRef.current.value = "";
      }
      return updated;
    });
  };

  const renderErrors = (errors) => {
    if (!errors || errors.length === 0) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200/60 text-red-700 px-4 py-3 rounded-2xl mb-4 shadow-sm"
      >
        <ul className="list-none space-y-1.5">
          {errors.map((error, index) => (
            <li key={index} className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" />
              <span className="text-sm font-medium">{error}</span>
            </li>
          ))}
        </ul>
      </motion.div>
    );
  };

  const uploadPropertyImagesHandler = (e) => {
    const files = [...e.target.files];
    const currentCount = propertyImages.filter((img) => !img.isExisting).length;

    if (currentCount + files.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} new images allowed`, {
        position: "bottom-right",
        duration: 3000,
      });
      return;
    }

    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      tag: null,
      aiLoading: true,
      isExisting: false,
      needsManualTag: false,
    }));

    const startIndex = propertyImages.length;
    setPropertyImages((prev) => [...prev, ...mapped]);

    mapped.forEach((img, i) => {
      classifyImageAI(img, startIndex + i, "property");
    });
  };

  const uploadAmenityImagesHandler = (e) => {
    const files = [...e.target.files];
    const currentCount = amenityImages.filter((img) => !img.isExisting).length;

    if (currentCount + files.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} new images allowed`, {
        position: "bottom-right",
        duration: 3000,
      });
      return;
    }

    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      tag: null,
      aiLoading: true,
      isExisting: false,
      needsManualTag: false,
    }));

    const startIndex = amenityImages.length;
    setAmenityImages((prev) => [...prev, ...mapped]);

    mapped.forEach((img, i) => {
      classifyImageAI(img, startIndex + i, "amenity");
    });
  };

  const uploadPropertyInfoFiles = (e) => {
    const files = [...e.target.files];
    if (!files.length) return;

    setPropertyInfoFiles(files.slice(0, 1));
    setRemovePropertyInfo(false);

    e.target.value = "";
  };

  const uploadBuildingInfoFiles = (e) => {
    const files = [...e.target.files];
    if (!files.length) return;

    setBuildingInfoFiles(files.slice(0, 1));
    setRemoveBuildingInfo(false);

    e.target.value = "";
  };

  useEffect(() => {
    if (handoverQuarter && handoverYear) {
      setHandoverBy(`${handoverQuarter} ${handoverYear}`);
    }
  }, [handoverQuarter, handoverYear]);

  useEffect(() => {
    return () => {
      propertyImages.forEach((img) => {
        if (img.preview && !img.isExisting) {
          URL.revokeObjectURL(img.preview);
        }
      });
      amenityImages.forEach((img) => {
        if (img.preview && !img.isExisting) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, []);

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-center items-center p-4"
          onClick={closeModal}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-3xl w-full max-w-[1000px] max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}
            <div className="bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Edit Listing</h2>
                <p className="text-sm text-white/80">Update property details</p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                disabled={loading}
              >
                <IoCloseOutline size={24} />
              </button>
            </div>

            {/* FORM CONTENT */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* FORM ERRORS */}
                {formErrors.length > 0 && renderErrors(formErrors)}
                {apiErrors.length > 0 && renderErrors(apiErrors)}

                {/* UNKNOWN IMAGES WARNING */}
                {hasUnknownTags() && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-2xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-bold">Action Required</p>
                        <p className="text-sm mt-1">
                          {unknownImagesCount()} image(s) need categorization.
                          Select a category for images marked as "Unknown".
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* FORM SECTIONS */}
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                  {/* LEFT COLUMN */}
                  <div className="space-y-6">
                    {/* PROPERTY NAME */}
                    <div>
                      <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                        <Home className="w-4 h-4 text-[#5563F6]" />
                        Property Name *
                      </label>
                      <input
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                        value={title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        placeholder="Enter property name"
                        disabled={loading}
                      />
                    </div>

                    {/* DESCRIPTION */}
                    <div>
                      <label className="block mb-2 font-semibold text-gray-900">
                        Property Description
                      </label>
                      <textarea
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50 resize-none"
                        rows={4}
                        value={description}
                        onChange={(e) =>
                          handleDescriptionChange(e.target.value)
                        }
                        placeholder="Describe your property..."
                        disabled={loading}
                      />
                    </div>

                    {/* PROPERTY CLASS & TYPE */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block mb-2 font-semibold text-gray-900">
                          Property Class *
                        </label>
                        <div className="relative">
                          <select
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                            value={propertyClass}
                            onChange={(e) => setPropertyClass(e.target.value)}
                            disabled={loading}
                          >
                            {PROPERTY_CLASS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <svg
                              className="w-4 h-4 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block mb-2 font-semibold text-gray-900">
                          Property Type *
                        </label>
                        <div className="relative">
                          <select
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                            value={propertyType}
                            onChange={(e) => setPropertyType(e.target.value)}
                            disabled={loading}
                          >
                            {PROPERTY_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <svg
                              className="w-4 h-4 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* BED & BATH */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                          <Users className="w-4 h-4 text-[#5563F6]" />
                          Bedrooms *
                        </label>
                        <div className="relative">
                          <select
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                            value={bedroom}
                            onChange={(e) => setBedroom(e.target.value)}
                            disabled={loading}
                          >
                            {BEDROOM_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <svg
                              className="w-4 h-4 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block mb-2 font-semibold text-gray-900">
                          Bathrooms
                        </label>
                        <div className="relative">
                          <select
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                            value={bathroom}
                            onChange={(e) => setBathroom(e.target.value)}
                            disabled={loading}
                          >
                            {BATHROOM_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <svg
                              className="w-4 h-4 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                        <Ruler className="w-4 h-4 text-[#5563F6]" />
                        Size (sq ft) *
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                        placeholder="Enter property size"
                        value={size}
                        onChange={(e) => handleSizeChange(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                    {/* PRICE FIELDS */}
                    {listingType === "Rent" ? (
                      <div>
                        <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                          <CurrencyIcon className="w-4 h-4 text-[#5563F6]" />
                          Rental Price *
                        </label>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block mb-1 text-sm text-gray-600 flex items-center">
                              Monthly (<CurrencyIcon className="w-3 h-3" />)
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                              value={monthlyRent}
                              onChange={(e) =>
                                handleMonthlyRentChange(e.target.value)
                              }
                              placeholder="0.00"
                              disabled={loading}
                            />
                          </div>

                          <div>
                            <label className="block mb-1 text-sm text-gray-600 flex items-center">
                              Yearly (<CurrencyIcon className="w-3 h-3" />)
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                              value={yearlyRent}
                              onChange={(e) =>
                                handleYearlyRentChange(e.target.value)
                              }
                              placeholder="0.00"
                              disabled={loading}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-[#5563F6]" />
                          Sale Price ($) *
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                          value={sellPrice}
                          onChange={(e) =>
                            handleSellPriceChange(e.target.value)
                          }
                          placeholder="Enter sale price"
                          disabled={loading}
                        />
                      </div>
                    )}

                    <div>
                      <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#5563F6]" />
                        Location *
                      </label>
                      <div className="city-dropdown-container space-y-3">
                        <div className="relative">
                          <input
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50 pr-10"
                            placeholder="Search city..."
                            value={city}
                            onChange={(e) => {
                              setCity(e.target.value);
                              setShowCityDropdown(true);
                            }}
                            onFocus={() => setShowCityDropdown(true)}
                            disabled={loading}
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <svg
                              className="w-4 h-4 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                          </div>

                          {showCityDropdown && (
                            <div className="absolute w-full bg-white border border-gray-200 rounded-xl shadow-lg z-50 mt-1 max-h-60 overflow-y-auto">
                              {cities
                                .filter((item) =>
                                  item.city
                                    .toLowerCase()
                                    .includes(city.toLowerCase())
                                )
                                .slice(0, 50)
                                .map((item, index) => (
                                  <button
                                    key={`${item.city}-${index}`}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 transition-colors"
                                    onClick={() => {
                                      setCity(item.city);
                                      setShowCityDropdown(false);
                                    }}
                                  >
                                    {item.city}
                                  </button>
                                ))}

                              {cities.filter((item) =>
                                item.city
                                  .toLowerCase()
                                  .includes(city.toLowerCase())
                              ).length === 0 && (
                                <div className="px-4 py-3 text-gray-400 text-center">
                                  No cities found
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <input
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                          value={address}
                          onChange={(e) => handleAddressChange(e.target.value)}
                          placeholder="Full address"
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block mb-2 font-semibold text-gray-900">
                          Tour Duration *
                        </label>
                        <div className="relative">
                          <select
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                            value={isCustomTour ? "custom" : tourDuration}
                            onChange={(e) => {
                              if (e.target.value === "custom") {
                                setIsCustomTour(true);
                                setCustomTourDuration("");
                              } else {
                                setIsCustomTour(false);
                                setTourDuration(e.target.value);
                              }
                            }}
                            disabled={loading}
                          >
                            {TOUR_DURATIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <svg
                              className="w-4 h-4 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>

                        {isCustomTour && (
                          <input
                            type="number"
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 mt-2 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                            placeholder="Enter custom duration (minutes)"
                            value={customTourDuration}
                            onChange={(e) =>
                              setCustomTourDuration(e.target.value)
                            }
                            disabled={loading}
                            min="1"
                          />
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 font-semibold text-gray-900">
                          Buffer Time *
                        </label>
                        <div className="relative">
                          <select
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                            value={isCustomBuffer ? "custom" : bufferTime}
                            onChange={(e) => {
                              if (e.target.value === "custom") {
                                setIsCustomBuffer(true);
                                setCustomBufferTime("");
                              } else {
                                setIsCustomBuffer(false);
                                setBufferTime(e.target.value);
                              }
                            }}
                            disabled={loading}
                          >
                            {BUFFER_TIMES.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <svg
                              className="w-4 h-4 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>

                        {isCustomBuffer && (
                          <input
                            type="number"
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 mt-2 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                            placeholder="Enter custom buffer (minutes)"
                            value={customBufferTime}
                            onChange={(e) =>
                              setCustomBufferTime(e.target.value)
                            }
                            disabled={loading}
                            min="1"
                          />
                        )}
                      </div>
                    </div>

                    {listingType === "Rent" && (
                      <div>
                        <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-[#5563F6]" />
                          Availability
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block mb-1 text-sm text-gray-600">
                              Available From
                            </label>
                            <input
                              type="date"
                              className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                              value={availableFrom}
                              onChange={(e) => setAvailableFrom(e.target.value)}
                              disabled={loading}
                              min={today}
                            />
                          </div>
                          <div>
                            <label className="block mb-1 text-sm text-gray-600">
                              Available Till
                            </label>
                            <input
                              type="date"
                              className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                              value={availableTo}
                              onChange={(e) => setAvailableTo(e.target.value)}
                              disabled={loading}
                              min={availableFrom || today}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#5563F6]" />
                    Documents
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block mb-2 font-semibold text-gray-900">
                        Building Information
                      </label>

                      {existingBuildingInfo && !removeBuildingInfo && (
                        <div className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-xl mb-3">
                          <a
                            href={existingBuildingInfo.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#5563F6] hover:text-[#4453E3] font-medium truncate flex items-center gap-2"
                          >
                            <FileText className="w-4 h-4" />
                            {existingBuildingInfo.name}
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              setExistingBuildingInfo(null);
                              setRemoveBuildingInfo(true);
                              if (buildingInfoInputRef.current) {
                                buildingInfoInputRef.current.value = "";
                              }
                            }}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-lg"
                          >
                            <IoCloseOutline size={18} />
                          </button>
                        </div>
                      )}

                      {!existingBuildingInfo && (
                        <label className="block cursor-pointer">
                          <div className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-8 text-center hover:border-[#5563F6] hover:bg-[#5563F6]/5 transition-all">
                            <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-3">
                              <FileText className="w-6 h-6 text-[#5563F6]" />
                            </div>
                            <p className="font-medium text-gray-700">
                              Upload Building Info
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              PDF, Excel, Word documents
                            </p>
                          </div>
                          <input
                            type="file"
                            accept=".pdf,.xlsx,.xls,.doc,.docx"
                            className="hidden"
                            onChange={uploadBuildingInfoFiles}
                            disabled={loading}
                            ref={buildingInfoInputRef}
                          />
                        </label>
                      )}

                      {buildingInfoFiles.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {buildingInfoFiles.map((file, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between bg-gray-50 px-4 py-2.5 rounded-lg"
                            >
                              <span className="text-sm text-gray-700 flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                {file.name}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeBuildingInfoFile(0)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <IoCloseOutline size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block mb-2 font-semibold text-gray-900">
                        Property Information
                      </label>

                      {existingPropertyInfo && !removePropertyInfo && (
                        <div className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-xl mb-3">
                          <a
                            href={existingPropertyInfo.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#5563F6] hover:text-[#4453E3] font-medium truncate flex items-center gap-2"
                          >
                            <FileText className="w-4 h-4" />
                            {existingPropertyInfo.name}
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              setExistingPropertyInfo(null);
                              setRemovePropertyInfo(true);
                              if (propertyInfoInputRef.current) {
                                propertyInfoInputRef.current.value = "";
                              }
                            }}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-lg"
                          >
                            <IoCloseOutline size={18} />
                          </button>
                        </div>
                      )}

                      {!existingPropertyInfo && (
                        <label className="block cursor-pointer">
                          <div className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-8 text-center hover:border-[#5563F6] hover:bg-[#5563F6]/5 transition-all">
                            <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-3">
                              <FileText className="w-6 h-6 text-[#5563F6]" />
                            </div>
                            <p className="font-medium text-gray-700">
                              Upload Property Info
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              PDF, Excel, Word, Images
                            </p>
                          </div>
                          <input
                            type="file"
                            accept=".pdf,.xlsx,.xls,image/*,.doc,.docx"
                            className="hidden"
                            onChange={uploadPropertyInfoFiles}
                            disabled={loading}
                            ref={propertyInfoInputRef}
                          />
                        </label>
                      )}

                      {propertyInfoFiles.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {propertyInfoFiles.map((file, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between bg-gray-50 px-4 py-2.5 rounded-lg"
                            >
                              <span className="text-sm text-gray-700 flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                {file.name}
                              </span>
                              <button
                                type="button"
                                onClick={() => removePropertyInfoFile(idx)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                                disabled={loading}
                              >
                                <IoCloseOutline size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-[#5563F6]" />
                    Images
                  </h3>

                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <label className="font-semibold text-gray-900">
                          Property Images
                        </label>
                        <p className="text-sm text-gray-500">
                          Indoor and outdoor spaces
                        </p>
                      </div>
                      <span className="text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                        {propertyImages.filter((img) => !img.isExisting).length}
                        /{MAX_IMAGES} new
                      </span>
                    </div>
                    <label
                      className="block cursor-pointer mb-4"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        uploadPropertyImagesHandler({
                          target: { files: e.dataTransfer.files },
                        });
                      }}
                    >
                      <div className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-8 text-center hover:border-[#5563F6] hover:bg-[#5563F6]/5 transition-all">
                        <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-3">
                          <ImageIcon className="w-6 h-6 text-[#5563F6]" />
                        </div>
                        <p className="font-medium text-gray-700">
                          Add Property Images
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Click or drag to upload
                        </p>
                      </div>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={uploadPropertyImagesHandler}
                        disabled={
                          loading ||
                          propertyImages.filter((img) => !img.isExisting)
                            .length >= MAX_IMAGES
                        }
                        ref={propertyImagesInputRef}
                      />
                    </label>
                    {propertyImages.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {propertyImages.map((img, index) => (
                          <div key={index} className="space-y-2">
                            <div
                              className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                                img.markedForRemoval
                                  ? "border-red-500 opacity-50"
                                  : !img.aiLoading &&
                                    (!img.tag || img.tag === "unknown")
                                  ? "border-red-400"
                                  : img.tag && img.tag !== "unknown"
                                  ? "border-green-400"
                                  : "border-gray-300"
                              }`}
                            >
                              <div className="relative w-full h-full">
                                <img
                                  src={img.preview || img.image}
                                  className="w-full h-full object-cover"
                                  alt={`Property ${index + 1}`}
                                />
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeImage(index, "property");
                                  }}
                                  className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                                    img.markedForRemoval
                                      ? "bg-gray-500 text-white"
                                      : "bg-red-500 text-white hover:bg-red-600"
                                  }`}
                                  disabled={loading}
                                  title={
                                    img.markedForRemoval
                                      ? "Will be removed"
                                      : "Remove"
                                  }
                                >
                                  <IoCloseOutline size={14} />
                                </button>
                                {img.aiLoading && (
                                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="space-y-1">
                              <TagSelect
                                value={img.tag}
                                onChange={(tag) =>
                                  updateImageTag(index, "property", tag)
                                }
                                options={OUTDOOR_TAGS}
                                placeholder={
                                  img.aiLoading
                                    ? "Analyzing..."
                                    : "Select category"
                                }
                                disabled={img.aiLoading || img.markedForRemoval}
                                type="property"
                                isUnknown={
                                  !img.aiLoading &&
                                  (!img.tag || img.tag === "unknown")
                                }
                              />

                              <div className="text-xs text-center">
                                {img.markedForRemoval ? (
                                  <span className="text-red-500 font-medium flex items-center justify-center gap-1">
                                    <IoCloseOutline className="w-3 h-3" />
                                    To be removed
                                  </span>
                                ) : img.aiLoading ? (
                                  <span className="text-gray-400 animate-pulse flex items-center justify-center gap-1">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Analyzing…
                                  </span>
                                ) : img.tag && img.tag !== "unknown" ? (
                                  <span className="text-green-600 font-medium flex items-center justify-center gap-1">
                                    <Check className="w-3 h-3" />
                                    {OUTDOOR_TAGS.find(
                                      (t) => t.value === img.tag
                                    )?.label || img.tag}
                                  </span>
                                ) : (
                                  <span className="text-red-500 font-medium flex items-center justify-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    Select category
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <label className="font-semibold text-gray-900">
                          Amenity Images
                        </label>
                        <p className="text-sm text-gray-500">
                          Common areas and facilities
                        </p>
                      </div>
                      <span className="text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                        {amenityImages.filter((img) => !img.isExisting).length}/
                        {MAX_IMAGES} new
                      </span>
                    </div>
                    <label
                      className="block cursor-pointer mb-4"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        uploadAmenityImagesHandler({
                          target: { files: e.dataTransfer.files },
                        });
                      }}
                    >
                      <div className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-8 text-center hover:border-[#5563F6] hover:bg-[#5563F6]/5 transition-all">
                        <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-3">
                          <Building className="w-6 h-6 text-[#5563F6]" />
                        </div>
                        <p className="font-medium text-gray-700">
                          Add Amenity Images
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Click or drag to upload
                        </p>
                      </div>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={uploadAmenityImagesHandler}
                        disabled={
                          loading ||
                          amenityImages.filter((img) => !img.isExisting)
                            .length >= MAX_IMAGES
                        }
                        ref={amenityImagesInputRef}
                      />
                    </label>
                    {amenityImages.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {amenityImages.map((img, index) => (
                          <div key={index} className="space-y-2">
                            <div
                              className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                                img.markedForRemoval
                                  ? "border-red-500 opacity-50"
                                  : !img.aiLoading &&
                                    (!img.tag || img.tag === "unknown")
                                  ? "border-red-400"
                                  : img.tag && img.tag !== "unknown"
                                  ? "border-green-400"
                                  : "border-gray-300"
                              }`}
                            >
                              <div className="relative w-full h-full">
                                <img
                                  src={img.preview || img.image}
                                  className="w-full h-full object-cover"
                                  alt={`Amenity ${index + 1}`}
                                />
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeImage(index, "amenity");
                                  }}
                                  className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                                    img.markedForRemoval
                                      ? "bg-gray-500 text-white"
                                      : "bg-red-500 text-white hover:bg-red-600"
                                  }`}
                                  disabled={loading}
                                  title={
                                    img.markedForRemoval
                                      ? "Will be removed"
                                      : "Remove"
                                  }
                                >
                                  <IoCloseOutline size={14} />
                                </button>
                                {img.aiLoading && (
                                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="space-y-1">
                              <TagSelect
                                value={img.tag}
                                onChange={(tag) =>
                                  updateImageTag(index, "amenity", tag)
                                }
                                options={INDOOR_TAGS}
                                placeholder={
                                  img.aiLoading
                                    ? "Analyzing..."
                                    : "Select category"
                                }
                                disabled={img.aiLoading || img.markedForRemoval}
                                type="amenity"
                                isUnknown={
                                  !img.aiLoading &&
                                  (!img.tag || img.tag === "unknown")
                                }
                              />

                              <div className="text-xs text-center">
                                {img.markedForRemoval ? (
                                  <span className="text-red-500 font-medium flex items-center justify-center gap-1">
                                    <IoCloseOutline className="w-3 h-3" />
                                    To be removed
                                  </span>
                                ) : img.aiLoading ? (
                                  <span className="text-gray-400 animate-pulse flex items-center justify-center gap-1">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Analyzing…
                                  </span>
                                ) : img.tag && img.tag !== "unknown" ? (
                                  <span className="text-green-600 font-medium flex items-center justify-center gap-1">
                                    <Check className="w-3 h-3" />
                                    {INDOOR_TAGS.find(
                                      (t) => t.value === img.tag
                                    )?.label || img.tag}
                                  </span>
                                ) : (
                                  <span className="text-red-500 font-medium flex items-center justify-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    Select category
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <motion.button
                    whileHover={{ scale: hasUnknownTags() ? 1 : 1.02 }}
                    whileTap={{ scale: hasUnknownTags() ? 1 : 0.98 }}
                    onClick={handleUpdate}
                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center ${
                      hasUnknownTags()
                        ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/20 cursor-not-allowed"
                        : "bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] hover:from-[#4453E3] hover:to-[#5A6AEA] text-white shadow-lg shadow-[#5563F6]/30"
                    }`}
                    disabled={loading || hasUnknownTags()}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                        Updating Listing...
                      </>
                    ) : hasUnknownTags() ? (
                      <>
                        <AlertCircle className="w-5 h-5 mr-2" />
                        Fix {unknownImagesCount()} image(s) first
                      </>
                    ) : (
                      "Update Listing"
                    )}
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {showHandoverModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
            onClick={() => setShowHandoverModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Select Handover Period
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-medium">
                      Quarter
                    </p>
                    {HANDOVER_QUARTERS.map((q) => (
                      <button
                        key={q}
                        onClick={() => setHandoverQuarter(q)}
                        className={`w-full py-3 rounded-lg mb-2 transition-all ${
                          handoverQuarter === q
                            ? "bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white shadow-lg"
                            : "bg-gray-100 hover:bg-gray-200"
                        }`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-medium">
                      Year
                    </p>
                    {HANDOVER_YEARS.map((y) => (
                      <button
                        key={y}
                        onClick={() => setHandoverYear(y)}
                        className={`w-full py-3 rounded-lg mb-2 transition-all ${
                          handoverYear === y
                            ? "bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white shadow-lg"
                            : "bg-gray-100 hover:bg-gray-200"
                        }`}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  className="mt-6 w-full py-3 bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white font-semibold rounded-xl hover:from-[#4453E3] hover:to-[#5A6AEA] transition-all shadow-lg"
                  onClick={() => setShowHandoverModal(false)}
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPaymentPlanModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
            onClick={() => setShowPaymentPlanModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Select Payment Plan
                </h3>
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {PAYMENT_PLANS.map((plan) => (
                    <button
                      key={plan}
                      onClick={() => {
                        setPaymentPlan(plan);
                        setShowPaymentPlanModal(false);
                      }}
                      className={`w-full py-3 px-4 rounded-lg transition-all text-left ${
                        paymentPlan === plan
                          ? "bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white shadow-lg"
                          : "bg-gray-100 hover:bg-gray-200"
                      }`}
                    >
                      {plan}
                    </button>
                  ))}
                </div>
                <button
                  className="mt-4 w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all"
                  onClick={() => setShowPaymentPlanModal(false)}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}